// Ported from relay-protocol packages/relay-widget (v0.1.0).
// Block-explorer links per chain. The catalog (OriginOption) carries only
// chainId/chainName/token -- no explorer URL -- so the token-detail link in
// SourceForm is derived here from a small chainId -> explorer map. Unknown
// chains return undefined (the UI then renders no link, gracefully).

interface Explorer {
  // base is the explorer origin WITHOUT a trailing slash.
  base: string
  // name is a short human label for the link (e.g. "BscScan").
  name: string
  // kind distinguishes the token-detail path shape: EVM explorers use
  // `/token/<address>`; Solana's Solscan uses `/token/<mint>` too, but is kept
  // separate so a future SVM explorer with a different path is a one-line change.
  kind: 'evm' | 'svm'
}

const explorers: Record<number, Explorer> = {
  1: { base: 'https://etherscan.io', name: 'Etherscan', kind: 'evm' },
  56: { base: 'https://bscscan.com', name: 'BscScan', kind: 'evm' },
  137: { base: 'https://polygonscan.com', name: 'PolygonScan', kind: 'evm' },
  8453: { base: 'https://basescan.org', name: 'BaseScan', kind: 'evm' },
  42161: { base: 'https://arbiscan.io', name: 'Arbiscan', kind: 'evm' },
  612055: { base: 'https://scan.crosstoken.io', name: 'ONEchain Scan', kind: 'evm' },
  792703809: { base: 'https://solscan.io', name: 'Solscan', kind: 'svm' },
}

/** explorerTokenLink returns the block-explorer token-detail URL + a short
 * explorer name for (chainId, tokenAddress), or undefined when the chain has
 * no known explorer (caller then renders no link). */
export function explorerTokenLink(chainId: number, address: string): { url: string; name: string } | undefined {
  const e = explorers[chainId]
  if (!e || !address) {
    return undefined
  }
  // Both EVM explorers and Solscan use `/token/<addr>` for the token page.
  return { url: `${e.base}/token/${address}`, name: e.name }
}

/** chainDisplayNames labels a chain in the transfer history's step rows.
 *
 * The catalog only names chains it lists as ORIGINS, but the history has to
 * label the bridge and delivery legs too (BSC and ONEchain), and ONEchain is
 * never an origin. Hence a small map here, with the chain id as an honest
 * fallback for anything unknown. */
const chainDisplayNames: Record<number, string> = {
  1: 'Ethereum',
  56: 'BNB Smart Chain',
  137: 'Polygon',
  8453: 'Base',
  42161: 'Arbitrum',
  612055: 'ONEchain',
  792703809: 'Solana',
}

/** chainDisplayName returns a human label for chainId, e.g. "Base". Falls back
 * to "Chain <id>" so an unlisted chain still renders something truthful. */
export function chainDisplayName(chainId: number): string {
  return chainDisplayNames[chainId] ?? `Chain ${chainId}`
}

/** explorerTxLink returns the block-explorer TRANSACTION URL + a short explorer
 * name for (chainId, txHash), or undefined when the chain has no known explorer
 * or the hash is empty (caller then renders plain text instead of a dead link).
 *
 * Separate from explorerTokenLink because the transfer history links one tx per
 * leg across THREE different chains -- the user's origin deposit, the BSC
 * bridge send, and the ONEchain delivery -- and each needs its own explorer. */
export function explorerTxLink(chainId: number, txHash: string): { url: string; name: string } | undefined {
  const e = explorers[chainId]
  if (!e || !txHash) {
    return undefined
  }
  // Solscan uses /tx/<signature> as well, so one shape covers both kinds today.
  return { url: `${e.base}/tx/${txHash}`, name: e.name }
}
